import HyperDX from '@hyperdx/browser';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';

import {
  ENV_CONFIG_BUILD_APP_NAME,
  ENV_CONFIG_HYPERDX_API_KEY,
  ENV_CONFIG_VITE_ENVIRONMENT,
  ReactRootAppName,
} from './common/constants/env';
import { applyStoredTheme } from './common/hooks/useTheme';

// Suppress known-noisy browser error events before HyperDX attaches its listener.
// Uses capture phase to run first; stopImmediatePropagation() prevents HyperDX from seeing them.
if (typeof window !== 'undefined') {
  applyStoredTheme();

  window.addEventListener(
    'error',
    (event) => {
      const msg = (event.message || '').toLowerCase();
      const isResizeObserver = msg.includes('resizeobserver loop');
      const isScriptError = msg === 'script error.'; // cross-origin, no stack trace
      if (isResizeObserver || isScriptError) {
        console.info('Suppressing known browser noise:', event.message);
        event.stopImmediatePropagation();
      }
    },
    { capture: true },
  );
}

if (ENV_CONFIG_HYPERDX_API_KEY) {
  HyperDX.init({
    apiKey: ENV_CONFIG_HYPERDX_API_KEY,
    service:
      ENV_CONFIG_BUILD_APP_NAME === ReactRootAppName.PmClient
        ? 'portalui'
        : 'bmsui',
    tracePropagationTargets: [
      /(?:bmsapi|pm-api|portalapi)\.getcone\.(dev|io)/i,
    ],
    consoleCapture: false, // we capture warn/error only via overrides below
    advancedNetworkCapture: false,
    disableReplay: false,
    maskAllText: true,
    ignoreUrls: [
      // Only track requests to Cone APIs — ignore all third-party traffic
      /^(?!https?:\/\/[^/]*\.getcone\.(dev|io|in))/,
    ],
    instrumentations: {
      interactions: false,
      longtask: false,
      // Disable resource/document/page tracking — high volume, low debug value.
      // ignoreUrls does NOT filter these; they read PerformanceResourceTiming directly.
      document: false, // eliminates resourceFetch, documentLoad, documentFetch spans (initial page load)
      postload: false, // eliminates resourceFetch spans for post-doc-load resources (lazy chunks, etc.)
      visibility: false, // eliminates visibility spans
      webvitals: false, // eliminates webvitals spans
    },
  });

  HyperDX.setGlobalAttributes({
    'deployment.environment':
      ENV_CONFIG_VITE_ENVIRONMENT === 'production' ? 'prod' : 'dev',
  });

  HyperDX.attachToReactErrorBoundary(ErrorBoundary);

  // Capture only console.warn and console.error — not info/log/debug
  const origWarn = console.warn;
  const origError = console.error;
  console.warn = (...args: Parameters<typeof console.warn>) => {
    const message = args.map(String).join(' ');
    // Suppress known third-party SDK noise — not actionable
    const isChatwootWsRestart = message.includes('Force restarting websocket');
    if (!isChatwootWsRestart) {
      HyperDX.addAction(`console.warn: ${message.slice(0, 150)}`, { message });
    }
    origWarn.apply(console, args);
  };
  console.error = (...args: Parameters<typeof console.error>) => {
    const message = args.map(String).join(' ');
    HyperDX.addAction(`console.error: ${message.slice(0, 150)}`, { message });
    origError.apply(console, args);
  };
}

const LazyPmAppIndex = React.lazy(() => import('./components/pm'));
const LazyPmClientAppIndex = React.lazy(() => import('../apps/pm-client/src'));

const getLazyAppIndexComponent = () => {
  const environmentsToIndexPathMap: Record<
    ReactRootAppName,
    React.LazyExoticComponent<() => JSX.Element>
  > = {
    PM: LazyPmAppIndex,
    PM_CLIENT_PORTAL: LazyPmClientAppIndex,
  };

  const rootAppName = ENV_CONFIG_BUILD_APP_NAME;

  return rootAppName
    ? environmentsToIndexPathMap[rootAppName] || environmentsToIndexPathMap.PM
    : environmentsToIndexPathMap.PM;
};

const LazyAppIndex = getLazyAppIndexComponent();

const container = document.getElementById('root');

if (!container) {
  console.error('Root container not found');
} else {
  const root = createRoot(container);
  root.render(
    <React.Suspense fallback={null}>
      <LazyAppIndex />
    </React.Suspense>,
  );
}
